import React, { useState, useEffect } from 'react'
import {
  Modal,
  Select,
  DatePicker,
  InputNumber,
  Table,
  Alert,
  Divider,
} from 'antd'
import moment from 'moment'
import { DERATE_TYPES, ZERO_CAP_TYPES } from './drlUtils'

const { Option } = Select
const HOURS = Array.from({ length: 24 }, (_, i) => i + 1)
const MINUTES = Array.from({ length: 60 }, (_, i) => i)

const RepeatUntilModal = ({
  visible,
  onCancel,
  onApprove,
  eventRows,
  selectedDate,
  selectedUtility,
  selectedUnit,
  unitHourlyRows,
  saveLoading,
  saveError,
}) => {
  const [selectedRowKey, setSelectedRowKey] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [startHour, setStartHour] = useState(1)
  const [endDate, setEndDate] = useState(null)
  const [endHour, setEndHour] = useState(24)
  const [endMinute, setEndMinute] = useState(0)
  const [derate, setDerate] = useState(null)
  const [validationError, setValidationError] = useState(null)

  useEffect(() => {
    if (!visible) return
    setSelectedRowKey(null)
    setStartDate(selectedDate ? selectedDate.clone() : moment())
    setStartHour(1)
    setEndDate(selectedDate ? selectedDate.clone() : moment())
    setEndHour(24)
    setEndMinute(0)
    setDerate(null)
    setValidationError(null)
  }, [visible, selectedDate])

  // When a derate row is selected, default start to the hour after its last active hour
  useEffect(() => {
    if (!visible) return
    const cand = candidates.find(c => c.rowKey === selectedRowKey)
    if (!cand || !DERATE_TYPES.has(cand.eventType)) return
    const nextHour = cand.lastHour < 24 ? cand.lastHour + 1 : 24
    setStartDate(selectedDate ? selectedDate.clone() : moment())
    setStartHour(nextHour)
  }, [selectedRowKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear validation error when any picker changes
  useEffect(() => {
    setValidationError(null)
  }, [selectedRowKey, startDate, startHour, endDate, endHour, endMinute])

  // Events that have a last active hour before H24 — candidates for extension
  const candidates = (eventRows || [])
    .filter(r => r.rowType === 'event')
    .map(r => {
      const hours = Object.keys(r.eventDataMap || {}).map(Number)
      const lastHour = hours.length > 0 ? Math.max(...hours) : 0
      const eventType = r.eventData?.eventType
      const causeCode = r.eventData?.causeCode
      const lastHourEvents = (r.eventDataMap || {})[lastHour] || []
      const lastMinute =
        lastHourEvents.length > 0
          ? Math.max(...lastHourEvents.map(e => moment(e.eventStart).minute()))
          : 0
      return {
        ...r,
        eventType,
        causeCode,
        lastHour,
        lastMinute,
        rowKey: `${eventType}|${causeCode}`,
      }
    })
    .filter(r => r.lastHour > 0 && r.lastHour < 24)

  const selected = candidates.find(c => c.rowKey === selectedRowKey)
  const isDerate = selected && DERATE_TYPES.has(selected.eventType)

  const unallocForStartHour = (() => {
    if (!isDerate || !unitHourlyRows || !startHour) return null
    const row = unitHourlyRows.find(r => r.metric === 'Unallocated')
    if (!row) return null
    const v = parseFloat(row[`h${startHour}`])
    return isNaN(v) ? null : v
  })()

  const computedAvailCap =
    isDerate && unallocForStartHour != null && derate != null
      ? Math.round((unallocForStartHour - derate) * 100) / 100
      : null

  const handleApprove = () => {
    if (!selected || !endDate) return

    const { lastHour, eventType, causeCode, eventDataMap } = selected

    let startMoment
    if (isDerate) {
      if (!startDate) return
      startMoment = startDate
        .clone()
        .hour(startHour - 1)
        .minute(0)
        .second(0)
      const endMomentCheck = endDate
        .clone()
        .hour(endHour - 1)
        .minute(endMinute)
        .second(0)
      if (!endMomentCheck.isAfter(startMoment)) {
        setValidationError(
          'End Date/Hour/Min must be after Start Date/Hour/Min'
        )
        return
      }
    } else {
      const isSameDay = endDate.isSame(selectedDate, 'day')
      const isBeforeDay = endDate.isBefore(selectedDate, 'day')
      if (isBeforeDay || (isSameDay && endHour <= lastHour)) {
        setValidationError(
          `End Date/Hour must be after ${selectedDate.format(
            'MM/DD/YYYY'
          )} ${String(lastHour).padStart(2, '0')}:00`
        )
        return
      }
      startMoment = selectedDate
        .clone()
        .hour(lastHour - 1)
        .minute(0)
        .second(0)
    }

    const unit = selectedUnit ? selectedUnit.substr(3) : selectedUnit
    const lastHourEvents = eventDataMap[lastHour] || []
    const baseEvent = lastHourEvents[0] || {}
    const endMoment = endDate
      .clone()
      .hour(endHour - 1)
      .minute(endMinute)
      .second(0)

    const payload = []
    // For derate: user specifies exact start → begin at startMoment
    // For non-derate: startMoment is the last filled hour → begin one hour after
    const cur = isDerate
      ? startMoment.clone()
      : startMoment.clone().add(1, 'hour')
    while (!cur.isAfter(endMoment)) {
      payload.push({
        utility: selectedUtility,
        unit,
        eventNum: -1,
        eventType,
        eventStart: cur.format('MM/DD/YYYY HH:mm'),
        availCap: isDerate
          ? computedAvailCap
          : ZERO_CAP_TYPES.has(eventType)
          ? 0
          : null,
        causeCode,
        coc: baseEvent.coc || null,
        description: baseEvent.description || null,
        isSubmitted: null,
      })
      cur.add(1, 'hour')
    }

    // Partial-hour terminator: ends the event at exact minute with cap=0
    if (endMinute > 0) {
      payload.push({
        utility: selectedUtility,
        unit,
        eventNum: -1,
        eventType,
        eventStart: endMoment.format('MM/DD/YYYY HH:mm'),
        availCap: 0,
        causeCode,
        coc: baseEvent.coc || null,
        description: baseEvent.description || null,
        isSubmitted: null,
      })
    }

    if (payload.length === 0) return
    onApprove(payload)
  }

  const columns = [
    {
      title: 'Date',
      key: 'date',
      width: 110,
      render: () => (selectedDate ? selectedDate.format('MM/DD/YYYY') : ''),
    },
    { title: 'Event Type', dataIndex: 'eventType', width: 110 },
    { title: 'Cause Code', dataIndex: 'causeCode', width: 110 },
    {
      title: 'Last Active Hour',
      dataIndex: 'lastHour',
      width: 130,
      render: (h, record) =>
        `${String(h).padStart(2, '0')}:${String(
          record.lastMinute || 0
        ).padStart(2, '0')}`,
    },
  ]

  const colHeader = {
    fontWeight: 600,
    fontSize: 12,
    color: 'rgba(0,0,0,0.55)',
    paddingBottom: 2,
  }
  const rowTitle = { fontWeight: 500, fontSize: 13, color: 'rgba(0,0,0,0.75)' }

  return (
    <Modal
      visible={visible}
      title="Repeat Until"
      onCancel={onCancel}
      onOk={handleApprove}
      okText="Approve"
      okButtonProps={{
        disabled: !selected || !endDate || (isDerate && !startDate),
        loading: saveLoading,
      }}
      width={620}
      maskClosable={false}
      zIndex={101}
    >
      <Table
        dataSource={candidates}
        columns={columns}
        rowKey="rowKey"
        size="small"
        pagination={false}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
          onChange: keys => setSelectedRowKey(keys[0] || null),
        }}
        onRow={record => ({
          onClick: () => setSelectedRowKey(record.rowKey),
          style: { cursor: 'pointer' },
        })}
        style={{ marginBottom: 16 }}
        locale={{
          emptyText: 'No events available to repeat (all events extend to H24)',
        }}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '50px 130px 80px 80px',
          columnGap: 10,
          rowGap: 8,
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        {/* Header row */}
        <div />
        <div style={colHeader}>Date</div>
        <div style={colHeader}>Hour</div>
        <div style={colHeader}>Min</div>

        {/* Start row — derate only */}
        {isDerate && (
          <>
            <div style={rowTitle}>Start</div>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              allowClear={false}
              format="MM/DD/YYYY"
              size="small"
              style={{ width: '100%' }}
            />
            <Select
              value={startHour}
              onChange={setStartHour}
              style={{ width: '100%' }}
            >
              {HOURS.map(h => (
                <Option key={h} value={h}>
                  {String(h).padStart(2, '0')}
                </Option>
              ))}
            </Select>
            <div />
          </>
        )}

        {/* End row */}
        <div style={rowTitle}>End</div>
        <DatePicker
          value={endDate}
          onChange={setEndDate}
          allowClear={false}
          format="MM/DD/YYYY"
          disabledDate={d =>
            d && selectedDate && d.isBefore(selectedDate, 'day')
          }
          style={{ width: '100%' }}
        />
        <Select value={endHour} onChange={setEndHour} style={{ width: '100%' }}>
          {HOURS.map(h => (
            <Option key={h} value={h}>
              {String(h).padStart(2, '0')}
            </Option>
          ))}
        </Select>
        <Select
          value={endMinute}
          onChange={setEndMinute}
          style={{ width: '100%' }}
        >
          {MINUTES.map(m => (
            <Option key={m} value={m}>
              {String(m).padStart(2, '0')}
            </Option>
          ))}
        </Select>
      </div>

      {isDerate && (
        <>
          <Divider style={{ margin: '8px 0 12px' }} />
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 16,
              marginBottom: 14,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {unallocForStartHour != null && (
                <span
                  style={{ fontWeight: 400, color: '#52c41a', fontSize: 12 }}
                >
                  {`Unallocated H${String(startHour).padStart(
                    2,
                    '0'
                  )}: ${unallocForStartHour.toFixed(1)}`}
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...rowTitle, flexShrink: 0 }}>Derate (MW):</span>
                <InputNumber
                  value={derate}
                  onChange={setDerate}
                  min={0}
                  style={{ width: 110 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...rowTitle, flexShrink: 0 }}>
                  Avail Cap (MW):
                </span>
                <InputNumber
                  value={computedAvailCap}
                  disabled
                  style={{ width: 110 }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {validationError && (
        <Alert
          type="error"
          message={validationError}
          showIcon
          style={{ marginBottom: 4 }}
        />
      )}

      {saveError && saveError.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {saveError.map((e, i) => (
            <Alert
              key={i}
              type={Number(e.severity) === 0 ? 'warning' : 'error'}
              message={<span dangerouslySetInnerHTML={{ __html: e.error }} />}
              showIcon
              style={{ marginBottom: 4 }}
            />
          ))}
        </div>
      )}
    </Modal>
  )
}

export default RepeatUntilModal
